import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import fileDownload from 'js-file-download';
import renderHTML from 'react-render-html';
import { Alert, AlertIcon, Box, Button, Spinner, Text } from '@chakra-ui/react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import AuthContext from '../contexts/AuthContext';
import contentGroupByIdQuery from '../graphql/queries/contentGroupById';
import contentGroupProgressQuery from '../graphql/queries/contentGroupProgress';

const ContentGroupCertificatePage = () => {
  const URL_CERTIFICATE = process.env.REACT_APP_CERTIFICATE;
  const { contentGroupId } = useParams();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [certificateOptions, setCertificateOptions] = useState({
    name: '',
    groupName: '',
    position: '',
    firstname: '-',
    lastname: '-',
    typesign: 'name',
    logo: 'http://localhost:3000/images/psuLogo.png',
    background: 'https://ppi-course.com/images/5.png',
    signature: '',
  });

  const { data: groupData, loading: groupLoading } = useQuery(contentGroupByIdQuery, {
    variables: { id: contentGroupId },
    fetchPolicy: 'network-only',
  });

  const { data: progressData, loading: progressLoading } = useQuery(contentGroupProgressQuery, {
    variables: { contentGroupId, studentId: user?._id },
    skip: !user?._id,
    fetchPolicy: 'network-only',
  });

  const group = groupData?.contentGroupId;
  const progress = progressData?.contentGroupProgress;
  const certificateEnabled = !!group?.certificateconfig?.status;
  const canDownload = certificateEnabled && progress?.progressPercent >= 100;

  useEffect(() => {
    if (!group || !user) return;
    const template = group.certificateconfig || {};
    const signature = template.signature || {};
    setCertificateOptions({
      name: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
      groupName: template.name || group.name || '',
      description: template.description || '',
      position: template.position || '',
      firstname: signature.firstname || '-',
      lastname: signature.lastname || '-',
      typesign: signature.typesign || 'name',
      logo: template.logoURL || 'http://localhost:3000/images/psuLogo.png',
      background: template.backgroundURL || 'https://ppi-course.com/images/5.png',
      signature: signature.signatureURL || '',
    });
  }, [group, user]);

  const certificateHtml = useMemo(
    () => `<!doctype html>
      <html lang="en-US">
      <head>
        <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
        <title>Certificate</title>
        <link href="https://fonts.googleapis.com/css?family=Satisfy" rel="stylesheet">
        <style type="text/css">
          @media print { html, body { margin: 0; } }
          html, body { margin: 0; }
          .cert {
            border: 13px solid #0072c6 !important;
            border-right: 13px solid #0894fb;
            border-left: 13px solid #0894fb;
            width: 200mm;
            height: 94mm;
            font-family: arial;
            color: #383737;
            background-image: url(${certificateOptions.background});
            background-repeat: no-repeat;
            background-size: cover;
            size: A4 landscape;
            margin: 0;
          }
          .crt_title { margin-top: 20px; font-family: "Satisfy", cursive; font-size: 40px; color: #0060a9; }
          .crt_logo img { width: 130px; height: auto; margin: auto; padding: 10px; }
          .colorGreen { color: #27ae60; }
          .crt_user {
            display: inline-block;
            width: 80%;
            padding: 5px 25px;
            font-family: "Satisfy", cursive;
            font-size: 40px;
            border-bottom: 1px dashed #cecece;
          }
          .certSign { width: 200px; }
        </style>
      </head>
      <body>
        <table class="cert">
          <tr>
            <td align="center" class="crt_logo">
              ${certificateOptions.logo ? `<img src="${certificateOptions.logo}" alt="logo"/>` : ''}
            </td>
          </tr>
          <tr>
            <td align="center">
              <h1 class="crt_title">Certificate Of Completion</h1>
              <h2>This Certificate is awarded to</h2>
              <h1 class="colorGreen crt_user">${certificateOptions.name}</h1>
              <h3>For completion of ${certificateOptions.groupName}</h3>
              ${certificateOptions.description ? `<h3>${certificateOptions.description}</h3>` : ''}
            </td>
          </tr>
          <tr>
            <td align="center">
              ${
                certificateOptions.typesign === 'signature' && certificateOptions.signature
                  ? `<img src="${certificateOptions.signature}" class="certSign" alt="sign" />`
                  : `<h3 style="font-style: italic;" class="certSign">${certificateOptions.firstname} ${certificateOptions.lastname}</h3>`
              }
              <h3>(${certificateOptions.firstname} ${certificateOptions.lastname})</h3>
              <h3>${certificateOptions.position}</h3>
            </td>
          </tr>
        </table>
      </body>
      </html>`,
    [certificateOptions]
  );

  const handleDownload = async () => {
    setLoading(true);
    try {
      const response = await axios.post(URL_CERTIFICATE, { content: certificateHtml, format: 'A4' }, { responseType: 'blob' });
      fileDownload(response?.data, `${certificateOptions.groupName || 'ContentGroup'}-Certificate.pdf`);
    } finally {
      setLoading(false);
    }
  };

  if (groupLoading || progressLoading) {
    return (
      <Box minH='80vh' display='flex' alignItems='center' justifyContent='center'>
        <Spinner />
      </Box>
    );
  }

  if (!canDownload) {
    return (
      <Box minH='80vh' maxW='3xl' mx='auto' mt={20} px={4}>
        <Alert status='warning' borderRadius='md'>
          <AlertIcon />
          <Text>ประกาศนียบัตรของกลุ่มเนื้อหานี้จะดาวน์โหลดได้เมื่อเปิดใช้งานและเรียนครบ 100%</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Fragment>
      <Box align='center' mt={20} minHeight='80vh'>
        <Button colorScheme='teal' variant='solid' minW={{ base: '80%', md: '360px' }} disabled={loading} onClick={handleDownload}>
          {!loading ? 'Download' : 'Downloading...'}
        </Button>
        <Box position='relative' w='100%' h='10.5cm' mt='25px'>
          {renderHTML(certificateHtml)}
        </Box>
      </Box>
    </Fragment>
  );
};

export default ContentGroupCertificatePage;
